import type { FetchData } from '@hooks/useDataFetcher';
import { useCallback, useState } from 'react';
import type { IMaster } from 'types/custom';

export type UseMasters = {
  deleteMaster: (master: IMaster, storeId: string) => Promise<void>;
  archiveMaster: (master: IMaster, storeId: string) => Promise<void>;
  getMastersByStoreId: (
    storeId: string,
    includeArchived?: boolean,
  ) => Promise<IMaster[]>;
  masters: {
    loading: boolean;
    list: { [storeId: string]: IMaster[] };
  };
  createMaster: (
    master: IMaster,
    storeId: string,
  ) => Promise<IMaster | undefined>;
  updateMaster: (
    master: IMaster,
    storeId: string,
  ) => Promise<IMaster | undefined>;
};

interface IState {
  masters: UseMasters['masters'];
}

export const useMasters = (fetchData: FetchData): UseMasters => {
  const [state, setState] = useState<IState>({
    masters: { loading: false, list: {} },
  });

  const createMaster: UseMasters['createMaster'] = useCallback(
    async (master, storeId) => {
      setState((state) => ({
        ...state,
        masters: { ...state.masters, loading: true },
      }));

      const createdMaster = await fetchData<IMaster>(
        `/api/offers/v1/admin/stores/${storeId}/masters`,
        undefined,
        'POST',
        master,
      );

      if (createdMaster?.id) {
        setState((state) => ({
          ...state,
          masters: {
            ...state.masters,
            loading: false,
            list: {
              ...state.masters.list,
              [storeId]: [
                { ...createdMaster, justCreated: true },
                ...(state.masters.list[storeId] ?? []),
              ],
            },
          },
        }));

        setTimeout(() => {
          setState((state) => ({
            ...state,
            masters: {
              ...state.masters,
              list: {
                ...state.masters.list,
                [storeId]: state.masters.list[storeId]?.map((master) => ({
                  ...master,
                  justCreated: undefined,
                })),
              },
            },
          }));
        }, 10 * 1000);

        return createdMaster;
      }

      setState((state) => ({
        ...state,
        masters: { ...state.masters, loading: false },
      }));
    },
    [fetchData],
  );

  const updateMaster: UseMasters['updateMaster'] = useCallback(
    async (master, storeId) => {
      setState((state) => ({
        ...state,
        masters: { ...state.masters, loading: true },
      }));

      const updatedMaster = await fetchData<IMaster>(
        `/api/offers/v1/admin/stores/${storeId}/masters/${master.id}`,
        undefined,
        'PUT',
        master,
      );

      if (updatedMaster?.id) {
        setState((state) => ({
          ...state,
          masters: {
            ...state.masters,
            loading: false,
            list: !updatedMaster
              ? state.masters.list
              : {
                  ...state.masters.list,
                  [storeId]: (state.masters.list[storeId] ?? []).map((m) =>
                    m.id === master.id ? updatedMaster : m,
                  ),
                },
          },
        }));

        return updatedMaster;
      }

      setState((state) => ({
        ...state,
        masters: { ...state.masters, loading: false },
      }));
    },
    [fetchData],
  );

  const getMastersByStoreId: UseMasters['getMastersByStoreId'] = useCallback(
    async (storeId, includeArchived = false) => {
      if (state.masters.list[storeId]?.length) {
        return state.masters.list[storeId].filter(
          (m) => includeArchived || !m.archived,
        );
      }

      setState((state) => ({
        ...state,
        masters: { ...state.masters, loading: true },
      }));

      const masters = await fetchData<IMaster[]>(
        `/api/offers/v1/admin/stores/${storeId}/masters?include_archived=true`,
      );

      setState((state) => ({
        ...state,
        masters: {
          ...state.masters,
          loading: false,
          list: !masters?.length
            ? state.masters.list
            : { ...state.masters.list, [storeId]: masters },
        },
      }));

      return masters?.length
        ? masters.filter((m) => includeArchived || !m.archived)
        : [];
    },
    [fetchData, state.masters.list],
  );

  const archiveMaster: UseMasters['archiveMaster'] = useCallback(
    async (master, storeId) => {
      setState((state) => ({
        ...state,
        masters: { ...state.masters, loading: true },
      }));

      const archivedMaster = await fetchData<IMaster>(
        `/api/offers/v1/admin/stores/${storeId}/masters/${master.id}/archive`,
        undefined,
        'POST',
      );

      setState((state) => ({
        ...state,
        masters: {
          ...state.masters,
          loading: false,
          list: {
            ...state.masters.list,
            [storeId]: archivedMaster
              ? [
                  archivedMaster,
                  ...state.masters.list[storeId]?.filter(
                    (m) => m.id !== master.id,
                  ),
                ]
              : state.masters.list[storeId]?.filter((m) => m.id !== master.id),
          },
        },
      }));
    },
    [fetchData],
  );

  const deleteMaster: UseMasters['deleteMaster'] = useCallback(
    async (master, storeId) => {
      setState((state) => ({
        ...state,
        masters: {
          ...state.masters,
          list: {
            ...state.masters.list,
            [storeId]: state.masters.list[storeId]?.filter(
              (m) => m.id !== master.id,
            ),
          },
        },
      }));

      await fetchData(
        `/api/offers/v1/admin/stores/${storeId}/masters/${master.id}`,
        undefined,
        'DELETE',
      );
    },
    [fetchData],
  );

  return {
    createMaster,
    deleteMaster,
    updateMaster,
    archiveMaster,
    getMastersByStoreId,
    masters: state.masters,
  };
};
